import { Button } from "@xola/ui-kit";
import { IFilterParams } from "ag-grid-community";
import { convertAnyStringToFormattedTime } from "common";
import { isEmpty } from "lodash";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Report, ReportObject, SelectionState } from "types";
import shallow from "zustand/shallow";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { getFormattedFilterModel, getReportFilterState } from "../CustomReportGrid";
import { ReportFilter, ReportFilterOperator, ReportFilterState, ReportFilterValue } from "../ReportFilterState";
import { CustomFilterSelector } from "./CustomFilterSelector";
import { formatCreatedAtEndDate } from "@/models/Report";
import { useCustomReportStore } from "@/stores/customReportFilterStore";

export const CustomFilter = forwardRef((props: IFilterParams, ref) => {
    const report = props.colDef.filterParams?.report as Report;
    const field = props.colDef.field ?? "partner";
    const [initialFilter, filter, setCurrentFilter] = useCustomReportStore(
        (state) => [state.initialFilter, state.currentFilter, state.setCurrentFilter],
        shallow,
    );
    const defaultValue =
        Array.isArray(filter[field]?.value) && (filter[field]?.value as ReportFilterValue[])?.length > 0
            ? { all: false, selected: (filter[field]?.value as ReportFilterValue[]).map(String) }
            : { all: true, selected: [] };
    const [value, setValue] = useState<SelectionState & { isEmpty?: boolean | null }>(defaultValue);
    const { collection, enableEmptySelection } = props.colDef.filterParams;

    useImperativeHandle(ref, () => ({
        isFilterActive() {
            if (Array.isArray(value.selected) && value.selected.length === 0) {
                console.log("%c isFilterActive: false", "color: orange");
                return false;
            }

            return !!value;
        },

        doesFilterPass() {
            return true; // Disables client-side filtering
        },

        getModel(): ReportFilter {
            if (Array.isArray(value.selected) && value.selected.length === 0) {
                console.log("%cThis should not be run", "color: red", value);
            }

            if (typeof value.isEmpty === "boolean" && value.selected.length === 0 && value.isEmpty !== value.all) {
                return {
                    operator: ReportFilterOperator.EXISTS,
                    value: !value.isEmpty,
                };
            }

            if (value.selected.length > 0 && value.isEmpty) {
                return {
                    operator: ReportFilterOperator.IN,
                    // Send empty for in values if isEmpty is enabled
                    value: [...value.selected, "empty"],
                };
            }

            return {
                operator: ReportFilterOperator.IN,
                value: value.selected,
            };
        },

        setModel(model: { in: ReportFilterOperator; value: string[] }) {
            if (!model) {
                console.log("%cShould not set filter because model.filter is undef", "color:#CC6600", model);
                setValue({ all: true, selected: [] });
                return false;
            }

            setValue({ all: model?.value?.length === 0 || !model?.value, selected: model?.value ?? [] });
        },
    }));

    const handleChange = (selectedState: SelectionState) => {
        setValue(selectedState);
    };

    const handleFilterChange = (filter: ReportFilterState) => {
        const localFilter = { ...filter };

        if (localFilter.product?.operator === ReportFilterOperator.IN && isEmpty(localFilter.product?.value)) {
            // Hack for removing "product[in]=[]" from the query params because Rushi can't figure out how to do it
            delete localFilter.product;
        }

        if (localFilter.partner?.operator === ReportFilterOperator.IN && isEmpty(localFilter.partner?.value)) {
            delete localFilter.partner;
        }

        if (
            localFilter["items.product"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["items.product"]?.value)
        ) {
            delete localFilter["items.product"];
        }

        if (
            localFilter["lineItems.coupon"]?.operator === ReportFilterOperator.IN &&
            isEmpty(localFilter["lineItems.coupon"]?.value)
        ) {
            delete localFilter["lineItems.coupon"];
        }

        if (report.object === ReportObject.TICKET_REDEMPTION && localFilter.createdAt) {
            localFilter.createdAt.value = formatCreatedAtEndDate(localFilter.createdAt.value as string[]);
        }

        if (localFilter?.arrivalTime) {
            localFilter.arrivalTime = {
                ...localFilter.arrivalTime,
                value: convertAnyStringToFormattedTime((localFilter?.arrivalTime.value as string).toString()), // TODO: FIX ME: I think we can just use `now()` here
            };
        }

        if (
            report.object === ReportObject.RESERVATIONS &&
            localFilter.status &&
            localFilter.status.operator === ReportFilterOperator.NIN
        ) {
            // Old saved reservations reports has NIN filter, now it is changed to IN filter, so removed old NIN
            delete localFilter.status;
        }

        if (report.object === ReportObject.RESERVATIONS_BY_LISTING && localFilter.arrivalDate) {
            // Filter arrival date by Item level also
            localFilter.items_arrivalDate = localFilter.arrivalDate;
        }

        setCurrentFilter(localFilter);
    };
    const getModel = () => {
        if (typeof value.isEmpty === "boolean" && value.selected.length === 0 && value.isEmpty !== value.all) {
            return {
                operator: ReportFilterOperator.EXISTS,
                value: !value.isEmpty,
            };
        }

        if (value.selected.length > 0 && value.isEmpty) {
            return {
                operator: ReportFilterOperator.IN,
                // Send empty for in values if isEmpty is enabled
                value: [...value.selected, "empty"],
            };
        }

        return {
            operator: ReportFilterOperator.IN,
            value: value.selected,
        };
    };
    const handleApply = () => {
        const model = getModel();
        const formattedFilterModel = getFormattedFilterModel({ [field]: model }, filter, initialFilter, report);
        handleFilterChange(getReportFilterState(formattedFilterModel));
        props.api.refreshHeader();
    };

    const handleCancel = () => {
        props.api.refreshHeader();
    };

    return (
        <div className="flex w-[400px] flex-col justify-between rounded-full p-2">
            <CustomFilterSelector
                value={value}
                collection={collection}
                enableEmptySelection={enableEmptySelection}
                onChange={handleChange}
            />
            <div className="space-x-2 p-2 text-right">
                <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>

                <Button onClick={handleApply}>Apply</Button>
            </div>
        </div>
    );
});
